/**
 * The colours and shapes the parent-facing screens use.
 *
 * The staff screens had grown their own palette — a navy (#04243c) that appears nowhere a parent
 * ever sees, three different "primary" colours across four files, and MUI's default 4px corners
 * against the parent side's 12px. Put beside the screens a parent uses, it read as a different
 * application that happened to share a logo.
 *
 * There is no MUI theme in this project (everything is written with `sx`), so these are plain
 * values rather than a palette. Import them instead of typing a hex code: the point is that the
 * next screen someone adds looks like the last one without having to go and read it.
 */

export const colour = {
    // The green everything acts through.
    primary: '#059669',
    primaryDark: '#047857',
    primaryDeep: '#065F46',
    primaryTint: '#ECFDF5',
    primaryTintBorder: '#A7F3D0',

    // Text, darkest to lightest.
    ink: '#0F172A',
    inkSoft: '#1E293B',
    body: '#334155',
    bodySoft: '#475569',
    muted: '#64748B',
    faint: '#94A3B8',

    // Surfaces and lines.
    border: '#E2E8F0',
    borderStrong: '#CBD5E1',
    stage: '#F8FAFC',
    panel: '#F1F5F9',
    surface: '#FFFFFF',

    // Saying something is wrong, or needs attention.
    dangerTint: '#FEF2F2',
    dangerBorder: '#EF4444',
    dangerInk: '#B91C1C',
    warnTint: '#FFFBEB',
    warnBorder: '#FCD34D',
    warnInk: '#92400E',
    infoTint: '#EFF6FF',
    infoBorder: '#BFDBFE',
    infoInk: '#1E40AF',
};

/**
 * The widest the content ever gets.
 *
 * 1240 came from matching the parent screens, where it is right: a parent reads prose and a form,
 * and a 1900px line of text is unreadable. Staff screens are tables of fifteen columns, and on a
 * large monitor the cap left the accounts table scrolling sideways inside a window with several
 * hundred pixels of empty stage either side of it.
 *
 * Raising the cap only affects screens wide enough to hit it: below this the content is already
 * `width: 100%`. Prose is held to its own measure separately — the lede on every staff page has
 * `maxWidth: 720` — so this widens tables without stretching sentences.
 */
export const CONTENT_WIDTH = 1800;

/** A white card on the tinted stage — the parent side's basic unit of layout. */
export const cardSx = {
    bgcolor: colour.surface,
    border: `1px solid ${colour.border}`,
    borderRadius: { xs: '12px', sm: '16px' },
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
};

/** The green button, with the glow the parent screens use. */
export const primaryButtonSx = {
    bgcolor: colour.primary,
    color: colour.surface,
    fontWeight: 700,
    textTransform: 'none',
    borderRadius: '12px',
    px: 3,
    boxShadow: '0 4px 14px rgba(5, 150, 105, 0.35)',
    '&:hover': { bgcolor: colour.primaryDark },
    '&.Mui-disabled': { bgcolor: colour.border, color: colour.faint },
};

export const secondaryButtonSx = {
    borderRadius: '12px',
    textTransform: 'none',
    fontWeight: 700,
    borderColor: colour.borderStrong,
    color: colour.bodySoft,
};

/**
 * The status pill. Six meanings, one shape — so "approved" and "waiting" are told apart by
 * colour and word rather than by position in a table.
 */
export const pillSx = (tone = 'neutral') => {
    const tones = {
        good: { bgcolor: colour.primaryTint, color: colour.primaryDeep, border: `1px solid ${colour.primaryTintBorder}` },
        warn: { bgcolor: colour.warnTint, color: colour.warnInk, border: `1px solid ${colour.warnBorder}` },
        bad: { bgcolor: colour.dangerTint, color: colour.dangerInk, border: `1px solid ${colour.dangerBorder}` },
        info: { bgcolor: colour.infoTint, color: colour.infoInk, border: `1px solid ${colour.infoBorder}` },
        neutral: { bgcolor: colour.panel, color: colour.bodySoft, border: `1px solid ${colour.border}` },
    };

    return { ...(tones[tone] || tones.neutral), fontWeight: 700, fontSize: '11.5px', height: '22px' };
};

/**
 * The table.
 *
 * DataGrid unstyled looks like a spreadsheet dropped into the page — grey gridlines, MUI's own
 * font sizes, a header that does not read as a header. This is the same grid wearing the parent
 * side's clothes.
 */
export const dataGridSx = {
    border: 'none',
    '& .MuiDataGrid-columnHeaders': {
        bgcolor: colour.stage,
        borderBottom: `1px solid ${colour.border}`,
        borderRadius: 0,
    },
    '& .MuiDataGrid-columnHeaderTitle': {
        fontWeight: 700,
        fontSize: '12.5px',
        color: colour.body,
    },
    '& .MuiDataGrid-cell': {
        borderBottom: `1px solid ${colour.border}`,
        fontSize: '13.5px',
        color: colour.body,
    },
    '& .MuiDataGrid-row:hover': { bgcolor: colour.stage },
    '& .MuiDataGrid-footerContainer': { borderTop: `1px solid ${colour.border}` },
    '& .MuiDataGrid-columnSeparator': { display: 'none' },
    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
    '& .MuiDataGrid-virtualScroller': { minHeight: '120px' },
};
